import { Component, ComponentFactoryResolver, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { BroadcastEventModel, BroadcastService, GridBaseComponent, ModalService, ModalSize, ObjectUtils, ToasterType } from '@cv-invision/core';
import { Subject } from 'rxjs';
import { LenderService } from '../service/lender.service';
import { CustomActionLenderCellComponent } from './custom-grid-cell/custom-action-lender-cell.component';
import { LenderColumnsModel } from './model/lender-column.model';
import { AcuityLenderBranch, DELETE_MAPPER_LENDER } from './model/lender.model';
import { GlobalConfigurationState } from '../state/global-configuration.state';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { CreateUpdateLenderComponent } from './create-update-lender/create-update-lender.component';

export const UPDATE_MAPPER_LENDER_LIST = 'UPDATE_MAPPER_LENDER_LIST';

@Component({
	selector: 'cv-lender',
	templateUrl: './lender.component.html',
	styleUrls: ['./lender.component.scss']
})
export class LenderComponent extends GridBaseComponent implements OnInit, OnDestroy {
	lenderConfiguration: any = {};
	acuityLenderBranchesList: Array<any>;
	selectedAppScopeBranch: Array<AcuityLenderBranch>;
	selectedAcuityBranch: Array<AcuityLenderBranch>;
	saving: boolean;
	isDirty: boolean;
	private atComponentDestroy: Subject<any> = new Subject<any>();
	private searchTextChange: EventEmitter<any> = new EventEmitter<any>();
	private paginationChangeFunctionId: any;


	filterLenders = {
		searchTextAcuityName: '',
		searchTextScopeName: '',
		pagination: {
			offset: 0,
			limit: 10,
			totalCount: 10
		}
	};

	constructor(
		private store: Store,
		public lenderService: LenderService,
		private broadcastService: BroadcastService,
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
	) {
		super();
		this.selectedAppScopeBranch = [];
		this.selectedAcuityBranch = [];
	}

	ngOnInit(): void {
		this.saving = false;
		this.isDirty = false;
		this.gridOptions.columnDefs = new LenderColumnsModel().columns;
		this.gridPagination = this.filterLenders;
		this.gridOptions.localeText = {
			noRowsToShow: 'No lenders to show',
			loadingOoo: 'Loading lenders...'
		}
		this.gridOptions.frameworkComponents = {
			customActionLenderCellComponent: CustomActionLenderCellComponent
		};
		this.acuityLenderBranchesList = this.store.selectSnapshot(GlobalConfigurationState.getAcuityLenderBranches);
		this.loadGridData();
		this.registerRefreshListListener();
		this.lenderService
			.getReloadListConfirmationListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result && result.reloadList) {
					this.loadGridData();
				}
			});
		this.registerDeleteMapperLenderListener();
		this.filtersEvents();


	}

	/**
	 *  Call service endpoint to search Lenders.
	 */
	loadGridData(): void {
		this.lenderService.getLenderConfiguration(this.filterLenders)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					if (result) {
						this.gridData = ObjectUtils.recursiveDeepCopy(result.mappers.data);
						this.gridPagination.pagination.offset = result.mappers.offset;
						this.gridPagination.pagination.totalCount = result.mappers.totalCount;
						const defaultBranch = ObjectUtils.recursiveDeepCopy(result);
						delete defaultBranch.mappers;
						this.lenderConfiguration = defaultBranch;
						this.convertLenderConfigurationDataToForm();
					}
				},
				error => console.error(error)
			);
	}

	filtersEvents(): void {
		this.searchTextChange
			.pipe(
				debounceTime(500),
				distinctUntilChanged()
			)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				this.gridApi.showLoadingOverlay();
				if (event.property === 'acuityName') {
					// eslint-disable-next-line no-useless-escape
					this.filterLenders.searchTextAcuityName = event.value.replace(/[&\/\\#,+()$~%'":*?<>{}]/g, '') || '';
				}

				if (event.property === 'scopeName') {
					// eslint-disable-next-line no-useless-escape
					this.filterLenders.searchTextScopeName = event.value.replace(/[&\/\\#,+()$~%'":*?<>{}]/g, '') || '';
				}
				this.loadGridData();
			});
	}

	/**
	 * Convert lender configuration data to form data
	 */
	convertLenderConfigurationDataToForm(): void {
		this.selectedAppScopeBranch = [{
			id: this.lenderConfiguration.defaultAcuityBranchId,
			title: this.lenderConfiguration.defaultAcuityBranchName
		}];

		this.selectedAcuityBranch = [{
			id: this.lenderConfiguration.defaultAcuityBranchIdWhenClass,
			title: this.lenderConfiguration.defaultAcuityBranchNameWhenClass
		}];

		this.gridData = this.gridData.map((mapper, key) => {
			return {
				id: key,
				...mapper
			};
		});
	}

	/**
	 * Select Acuity AppScope Branch
	 * @param $event
	 */
	onAppScopeBranchSelected($event: any): void {
		if ($event) {
			this.isDirty = true;
			this.lenderConfiguration.defaultAcuityBranchId = $event.id;
			this.lenderConfiguration.defaultAcuityBranchName = $event.title;
		}
	}

	/**
	 * Select Acuity ClassApi Branch
	 * @param $event
	 */
	onAcuityBranchSelected($event: any): void {
		if ($event) {
			this.isDirty = true;
			this.lenderConfiguration.defaultAcuityBranchIdWhenClass = $event.id;
			this.lenderConfiguration.defaultAcuityBranchNameWhenClass = $event.title;
		}
	}

	/**
	 * Save Lender Configuration
	 */
	saveConfiguration(type: string = 'update'): void {
		this.saving = true;
		this.lenderService
			.updateLenderConfiguration(this.lenderConfiguration)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				response => {
					if (response.succeeded) {
						const message = type === 'update' ?
							'The Lender Configuration Was Updated Successfully.' :
							'The Mapper Lender Was Deleted Successfully.';
						this.successHandleFunction(message);
						this.saving = false;
						this.isDirty = false;
					}
				},
				error => {
					this.errorMsgFunction(error.error.error);
				}
			);
	}

	/**
	 * handle response from server
	 */
	successHandleFunction(message: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: message }
		});
	}

	/**
	 * Display error message when the operation was not success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: { error }
		});
	}

	/**
	 * Register Lister to Delete Mapper Lender
	 *
	 */
	private registerDeleteMapperLenderListener(): void {
		this.broadcastService.onEvent(DELETE_MAPPER_LENDER)
		.pipe(takeUntil(this.atComponentDestroy))
		.subscribe(
			(broadcast: BroadcastEventModel) => {
				this.lenderService.deleteLender(broadcast.event).subscribe((response: any) => {
					if(response.succeeded){
						this.successHandleFunction("The Mapper Lender Was Deleted Successfully.");
						if(this.gridData.length < 2){
							this.restorePagination();
						}
						this.loadGridData();
					}
				})
			});
	}

	/**
	 * Register Lister to refresh mapper lender list
	 */
	private registerRefreshListListener(): void {
		this.broadcastService
			.onEvent(UPDATE_MAPPER_LENDER_LIST)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((e: BroadcastEventModel) => {
				this.loadGridData();
			});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Event to search
	 */

	onSearchTextChange(property: string, event: any): void {
		this.restorePagination();
		this.searchTextChange.emit({ property: property, value: event.value });
	}

	/**
	 * Override if needed.
	 * @param paginationModel
	 */
	onPaginationChange(paginationModel: PaginationModel): void {
		clearTimeout(this.paginationChangeFunctionId);
		this.paginationChangeFunctionId = setTimeout(() => {
			this.filterLenders.pagination = paginationModel;
			this.loadGridData();
		}, 250);
	}

	/**
	 * Restore pagination to initial
	 */
	private restorePagination(): void {
		this.filterLenders.pagination = {
			totalCount: 0,
			offset: 0,
			limit: 10
		};
	}

	/**
	 * Add new Lender
	 */
	onAddLender(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: CreateUpdateLenderComponent,
				data: {},
				modalConfiguration: {
					title: 'New Mapper Lender Configuration',
					modalSize: ModalSize.XL
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					this.loadGridData();
				} else {
					// it failed :(
				}
			});
	}
}
